import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
} from 'react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { AlertTriangle, Trash2, CheckCircle, Flame } from 'lucide-react-native';

interface EmptyExerciseWarningProps {
  visible: boolean;
  onClose: () => void;
  onFinish: () => void;
  onRemove: () => void;
  onSaveAnyway: () => void;
  emptyCount: number;
  nudgeTone?: 'motivational' | 'fun_trash_talk' | 'serious_direct';
}

export default function EmptyExerciseWarning({
  visible,
  onClose,
  onFinish,
  onRemove,
  onSaveAnyway,
  emptyCount,
  nudgeTone = 'motivational',
}: EmptyExerciseWarningProps) {
  const { theme } = useTheme();

  const getNudgeMessage = () => {
    switch (nudgeTone) {
      case 'motivational':
        return "You have exercises without recorded sets. Finish strong.";
      case 'fun_trash_talk':
        return "You started this workout — finish what you came for.";
      case 'serious_direct':
        return "Complete your workout for accurate tracking and progress.";
      default:
        return "You have exercises without recorded sets. Finish strong.";
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent
      onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={[styles.modal, { backgroundColor: theme.colors.surface }]}>
          <View style={[styles.iconContainer, { backgroundColor: theme.colors.warning + '20' }]}>
            <AlertTriangle size={32} color={theme.colors.warning} />
          </View>

          <Text style={[styles.title, { color: theme.colors.text }]}>
            Hold Up!
          </Text>

          <Text style={[styles.message, { color: theme.colors.textSecondary }]}>
            You still have {emptyCount} exercise{emptyCount > 1 ? 's' : ''} with no sets logged.
          </Text>

          <View style={[styles.motivationBox, { backgroundColor: theme.colors.accent + '10', borderColor: theme.colors.accent }]}>
            <Flame size={20} color={theme.colors.accent} />
            <Text style={[styles.motivationText, { color: theme.colors.text }]}>
              {getNudgeMessage()}
            </Text>
          </View>

          <View style={styles.actions}>
            <TouchableOpacity
              style={[
                styles.actionButton,
                styles.primaryButton,
                { backgroundColor: theme.colors.accent },
              ]}
              onPress={onFinish}>
              <CheckCircle size={20} color={theme.colors.accentForeground} />
              <Text style={[styles.primaryButtonText, { color: theme.colors.accentForeground }]}>
                Finish It
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[
                styles.actionButton,
                styles.secondaryButton,
                { borderColor: theme.colors.border, backgroundColor: theme.colors.surfaceVariant },
              ]}
              onPress={onRemove}>
              <Trash2 size={20} color={theme.colors.error} />
              <Text style={[styles.secondaryButtonText, { color: theme.colors.text }]}>
                Remove Empty
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.textButton]}
              onPress={onSaveAnyway}>
              <Text style={[styles.textButtonText, { color: theme.colors.textSecondary }]}>
                Save Anyway
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 2,
    padding: 24,
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  iconContainer: {
    width: 64,
    height: 64,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 12,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 16,
    lineHeight: 24,
  },
  motivationBox: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 2,
    borderWidth: 2,
    marginBottom: 24,
  },
  motivationText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    lineHeight: 20,
  },
  actions: {
    width: '100%',
    gap: 12,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 0,
  },
  primaryButton: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  primaryButtonText: {
    fontSize: 16,
    fontWeight: '700',
  },
  secondaryButton: {
    borderWidth: 1,
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
  textButton: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  textButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
